/**
 * App configuration sourced from the environment.
 *
 * Bun inlines `BUN_PUBLIC_*` variables into the client bundle at build time
 * (see `bunfig.toml` and `build.ts`), so these are the only env vars safe to
 * reference from front-end code. Copy `.env.example` to `.env` and fill in the
 * values from the Firebase console (Project settings → Your apps → SDK setup).
 */

export const FIREBASE_CONFIG = {
  apiKey: process.env.BUN_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.BUN_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.BUN_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.BUN_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.BUN_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.BUN_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.BUN_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

/** True once the minimum Firebase credentials are present in the environment. */
export const FIREBASE_ENABLED = Boolean(
  FIREBASE_CONFIG.apiKey && FIREBASE_CONFIG.projectId && FIREBASE_CONFIG.appId,
);

/**
 * pop.in public GraphQL endpoint. After a partner signs in with Firebase we hit
 * this to read their global roles and decide whether they may use the portal
 * (same backend the admin-gui talks to).
 */
export const GRAPHQL_ENDPOINT = process.env.BUN_PUBLIC_GRAPHQL_ENDPOINT;

/**
 * Base URL of the live pop.in web app used for brand previews. The portal embeds
 * `${PLAY_BASE_URL}/?brand_id=<brandId>` in an iframe to show partners their real
 * themed app, and pushes unsaved draft colours into it live via postMessage.
 * Set per-environment via `BUN_PUBLIC_PLAY_BASE_URL`. Falls back to the dev
 * preview host when unset (matches the dev backend brand IDs).
 *
 * IMPORTANT: this must be a host that serves the app **directly, without
 * redirecting**. `play-dev.pop.in` 301-redirects to `popin--preview.expo.app`,
 * which moves the iframe to a different origin and silently breaks the
 * postMessage live-theme channel (the browser drops sends whose `targetOrigin`
 * no longer matches the frame, and the readiness ping arrives from the wrong
 * origin). `preview.play.pop.in` (dev) serves the app in place, keeping the
 * origin stable. Prod uses `play.pop.in`.
 *
 * To preview against a locally-running popin-rn web build (`npm run web` in
 * popin-rn → http://localhost:8081), run the portal with `bun run dev:local-popin`
 * (or set `BUN_PUBLIC_PLAY_BASE_URL` in `.env.local`).
 */
export const PLAY_BASE_URL =
  process.env.BUN_PUBLIC_PLAY_BASE_URL ?? "https://preview.play.pop.in";

/**
 * reCAPTCHA Enterprise site key for Firebase App Check. Phone sign-in is gated
 * by App Check on the pop.in Firebase projects, so this must be set for the SMS
 * flow to work. When absent App Check is simply not initialized.
 */
export const RECAPTCHA_ENTERPRISE_KEY = process.env.BUN_PUBLIC_RECAPTCHA_ENTERPRISE_KEY;

/**
 * URL of the games-served **scene** the portal panel frames as a living surface
 * (Surface Protocol — see `src/features/scene`). The frame is scene-agnostic: it
 * hosts whatever guest answers the `surface/hello` handshake at this URL (the
 * GAIA dev scene today; games#277's arrival slate when it lands). Point it at a
 * local games dev server via `BUN_PUBLIC_SCENE_URL`; when the target is down the
 * frame shows an honest offline placard naming this URL (never fake chrome).
 *
 * Default is a local dev port so `bun dev` frames a locally-running scene; set
 * the env var to aim at the real games dev server's port.
 */
export const SCENE_URL = process.env.BUN_PUBLIC_SCENE_URL || "http://localhost:8787";

/**
 * Whether the dev-only living-scene route (`/dev/scene`) is mounted. This is a
 * dev harness behind a flag — it makes NO nav/menu IA change (menu IA is an open
 * product call); the route simply exists when enabled. On when
 * `BUN_PUBLIC_ENABLE_SCENE=true` (set in `.env.development`); off otherwise
 * (`.env.production` sets it `false`). The flag MUST be present in every loaded
 * `.env` file: Bun only inlines `BUN_PUBLIC_*` vars that exist, and a bare
 * `process.env.…` read throws in the browser bundle.
 */
export const SCENE_ENABLED = process.env.BUN_PUBLIC_ENABLE_SCENE === "true";

/** An opt-in env flag: on only when the value is exactly "true". */
export function envFlagOn(raw: string | undefined): boolean {
  return raw === "true";
}

/**
 * Whether `?fixtures=1` may swap the places reads for the made-up samples in
 * `src/features/places/fixtures.ts`. Opt-in, the same shape as
 * `SCENE_ENABLED`: on when `BUN_PUBLIC_ENABLE_FIXTURES=true`
 * (`.env.development`, which the dev preview build loads too), off otherwise
 * (`.env.production` sets it `false`). Absent means off, so a build with a
 * missing or new env file can never show sample numbers. Keep the key present
 * in every loaded `.env` file so Bun inlines it.
 */
export const FIXTURES_ENABLED = envFlagOn(process.env.BUN_PUBLIC_ENABLE_FIXTURES);

/**
 * Whether pop.in staff may schedule an event from a place's page (the portal's
 * one write to events; `src/features/places/schedule-event.ts`). Its own
 * opt-in flag, so sample data and real writes never ride together: on when
 * `BUN_PUBLIC_ENABLE_SCHEDULING=true` (`.env.development`, which the dev
 * preview build loads too), off otherwise (`.env.production` sets it `false`).
 * The page also requires a dev-api or local endpoint. Keep the key present in
 * every loaded `.env` file so Bun inlines it.
 */
export const SCHEDULING_ENABLED = envFlagOn(process.env.BUN_PUBLIC_ENABLE_SCHEDULING);

/** Global roles that are allowed into the Partner Portal. */
export const PARTNER_ROLES = ["partner", "super_admin"] as const;

/**
 * Global role that unlocks admin-only surfaces (the "All brands" directory and
 * cross-brand membership management). A super admin can see and edit every
 * brand, not just the ones they're a member of.
 */
export const SUPER_ADMIN_ROLE = "super_admin";
